import React, {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {Modal} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {useAuth} from '../../../modules/auth'
import {shouldDisplayFonctionnalite} from '../../../../components/helpers'
import {
  ModeleSavDossier,
  SavDetailResponse,
  SavMessagePrepare,
} from '../../../../components/sav/Models'
import {
  commenterSavDossier,
  envoyerSavMessage,
  preparerSavMessage,
  savErreur,
} from '../../../../components/sav/requests'

export const SAV_EVENEMENTS: Record<string, string> = {
  CREATION: 'Création du SAV',
  DEMANDE_REFERENCE: 'Demande de référence',
  REFUS: 'Refus motivé',
  BON_RETOUR: 'Bon de retour et consignes',
  RECEPTION: 'Colis reçu',
  NON_FONDE: 'Réclamation non fondée',
  AVOIR: 'Avoir émis',
  ECHANGE: 'Échange lancé',
}

type Props = {
  dossier: ModeleSavDossier
  // Code de l'événement ; null pour un message libre
  evenement: string | null
  // Texte de départ d'un message libre
  texteLibre?: string
  libelleBouton?: string
  // Action faite avant l'envoi (par exemple la clôture) ; l'envoi n'a pas lieu si elle échoue
  avantEnvoi?: () => Promise<unknown>
  // Toute valeur qui change le texte proposé (montant, n° d'avoir…) relance la préparation
  rafraichir?: unknown
  onTermine: (detail?: SavDetailResponse) => void
  onPasser?: () => void
}

// Carte « Informer le client » : modèle retenu, texte prérempli et modifiable pour cet envoi (spec 4.8)
const SavInformerClient = ({
  dossier,
  evenement,
  texteLibre = '',
  libelleBouton = 'Envoyer au client',
  avantEnvoi,
  rafraichir,
  onTermine,
  onPasser,
}: Props) => {
  const {currentUser} = useAuth()
  const [prepare, setPrepare] = useState<SavMessagePrepare | null>(null)
  const [modele, setModele] = useState<number | null>(null)
  const [canal, setCanal] = useState<'MAIL' | 'APPEL'>(
    dossier.modeContact === 'TELEPHONE' ? 'APPEL' : 'MAIL'
  )
  const [objet, setObjet] = useState('')
  const [corps, setCorps] = useState(texteLibre)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    preparerSavMessage(dossier.id, evenement, modele)
      .then((p) => {
        setPrepare(p)
        if (p.modele || !evenement) {
          setObjet(p.objet || (evenement ? '' : `Votre dossier SAV ${dossier.numero}`))
          setCorps(p.corps || texteLibre)
        }
      })
      .catch((e) => toast.error(savErreur(e)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dossier.id, evenement, modele, rafraichir])

  const restantes = Array.from(new Set(`${objet} ${corps}`.match(/\{[a-z_]+\}/g) ?? []))

  const envoyer = async () => {
    setEnvoi(true)
    try {
      if (avantEnvoi) await avantEnvoi()
      if (canal === 'MAIL') {
        const detail = await envoyerSavMessage(dossier.id, {
          modele: prepare?.modele,
          evenement,
          objet,
          corps,
        })
        if (detail.envoi?.statut === 'ECHEC') {
          toast.warning(
            "Le mail n'est pas parti : une tâche de relance a été créée dans Mes actions."
          )
        } else {
          toast.success('Message envoyé au client')
        }
        onTermine(detail)
      } else {
        const detail = await commenterSavDossier(
          dossier.id,
          `Appel client${
            evenement ? ` (${SAV_EVENEMENTS[evenement] ?? evenement})` : ''
          } : ${corps}`
        )
        toast.success('Appel noté dans le fil')
        onTermine(detail)
      }
    } catch (e) {
      toast.error(savErreur(e))
    } finally {
      setEnvoi(false)
    }
  }

  if (!prepare) return <div className='text-muted'>Préparation du message…</div>

  return (
    <div>
      <div className='mb-3'>
        <span className='text-muted'>Mode de contact souhaité : </span>
        <span className='fw-bold'>{dossier.modeContact === 'MAIL' ? 'Mail' : 'Téléphone'}</span>
      </div>
      <div className='btn-group mb-4'>
        <button
          type='button'
          className={`btn btn-sm ${canal === 'MAIL' ? 'btn-primary' : 'btn-light'}`}
          onClick={() => setCanal('MAIL')}
        >
          Mail
        </button>
        <button
          type='button'
          className={`btn btn-sm ${canal === 'APPEL' ? 'btn-primary' : 'btn-light'}`}
          onClick={() => setCanal('APPEL')}
        >
          Appel (noté dans le fil)
        </button>
        <button type='button' className='btn btn-sm btn-light' disabled title='Prévu en V2'>
          SMS · V2
        </button>
      </div>

      {evenement && (
        <div className='mb-3'>
          <label className='form-label'>Modèle de message</label>
          {prepare.modeles.length ? (
            <select
              className='form-select'
              value={prepare.modele ?? ''}
              onChange={(e) => setModele(e.target.value ? Number(e.target.value) : null)}
            >
              {prepare.modeles.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.libelle} · Mail · {m.entite ?? 'commun'}
                </option>
              ))}
            </select>
          ) : (
            <div className='text-muted'>Aucun modèle actif : message libre.</div>
          )}
        </div>
      )}

      {canal === 'MAIL' && (
        <>
          <div className='mb-3'>
            <span className='text-muted'>Destinataire : </span>
            {prepare.destinataire ? (
              <span className='fw-bold'>{prepare.destinataire}</span>
            ) : (
              <span className='text-danger'>aucune adresse mail sur le dossier</span>
            )}
          </div>
          <label className='form-label'>Objet</label>
          <input
            className='form-control form-control-solid mb-3'
            value={objet}
            onChange={(e) => setObjet(e.target.value)}
          />
        </>
      )}
      <label className='form-label'>
        {canal === 'MAIL' ? 'Message' : 'Ce qui a été dit au client'}
      </label>
      <textarea
        className='form-control form-control-solid mb-3'
        rows={7}
        value={corps}
        onChange={(e) => setCorps(e.target.value)}
      />
      {canal === 'MAIL' && restantes.length > 0 && (
        <div className='alert alert-warning py-2'>
          À compléter avant l'envoi : {restantes.join(', ')}
          {prepare.variablesVides.length > 0 && ' (donnée absente du dossier)'}.
        </div>
      )}
      <div className='alert alert-info py-2 fs-7'>
        Texte prérempli depuis le référentiel des messages SAV. Vos retouches ne s'appliquent qu'à
        cet envoi ; le modèle reste inchangé.
      </div>
      <div className='d-flex flex-wrap justify-content-between align-items-center gap-3'>
        {shouldDisplayFonctionnalite(currentUser, 'SAV_PARAMETRER') ? (
          <Link to='/sav/parametres/messages' className='fs-7'>
            Gérer les modèles
          </Link>
        ) : (
          <span />
        )}
        <div className='d-flex gap-2'>
          {onPasser && (
            <button type='button' className='btn btn-light' onClick={onPasser} disabled={envoi}>
              Ne pas informer
            </button>
          )}
          <button
            type='button'
            className='btn btn-success'
            disabled={
              envoi ||
              !corps.trim() ||
              (canal === 'MAIL' && (!prepare.destinataire || !objet.trim() || restantes.length > 0))
            }
            onClick={envoyer}
          >
            {envoi ? 'Envoi…' : libelleBouton}
          </button>
        </div>
      </div>
    </div>
  )
}

type ModalProps = Omit<Props, 'onPasser' | 'onTermine'> & {
  onClose: () => void
  onTermine?: (detail?: SavDetailResponse) => void
}

// Même carte en fenêtre modale, ouverte après une transition qui propose un message
export const SavInformerClientModal = ({onClose, onTermine, ...props}: ModalProps) => (
  <Modal show onHide={onClose} size='lg' centered>
    <Modal.Header closeButton>
      <Modal.Title>
        Informer le client
        {props.evenement ? ` · ${SAV_EVENEMENTS[props.evenement] ?? props.evenement}` : ''}
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <SavInformerClient
        {...props}
        onPasser={onClose}
        onTermine={(detail) => {
          onTermine?.(detail)
          onClose()
        }}
      />
    </Modal.Body>
  </Modal>
)

export default SavInformerClient
